/*
 * RestaurantMap.jsx — a personal restaurant & cafe map (read-only)
 * ---------------------------------------------------------------
 * Drop into your React portfolio as a tab/route. Self-contained.
 * All data comes from PLACES (import your own JSON). Read-only —
 * no add/edit/delete UI. Each place may carry an `images` array
 * that renders as a small photo collage in the detail card.
 *
 * Install:
 *   npm i react-leaflet leaflet
 * Import Leaflet's CSS once (here or in your app entry):
 *   import "leaflet/dist/leaflet.css";
 *
 * TILES — pick ONE:
 *   1) Free, no key (default): CARTO "light" base + a warm CSS filter
 *      on the tile pane, giving the beige/cream look.
 *   2) Mapbox custom warm style: set MAPBOX_TOKEN + MAPBOX_STYLE below.
 */

import React, { useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/* ---- config ------------------------------------------------------------ */
// const OWNER = "priyam maini";
const TAGLINE = "places i keep coming back to";
const CENTER = [28.59, 77.21]; // Delhi
const ZOOM = 12;
const WARMTH = 0.5; // 0..1 — strength of the warm tile filter

const REST = "#c2694a"; // terracotta
const CAFE = "#d69a3c"; // amber
const SEL = "#3f7fae"; // blue (selected)
const INK = "#1a1512"; // near-black warm (dark UI)

// Paste your Mapbox token here to use a custom warm style instead of CARTO.
const MAPBOX_TOKEN = ""; // e.g. "pk.eyJ1Ijoi..."
const MAPBOX_STYLE = "mapbox/light-v11"; // or your own custom style id

/* ---- data -------------------------------------------------------------- */
/* Replace with `import PLACES from "./places.json";`
 * Shape per place: { id, name, emoji, type:"restaurant"|"cafe",
 *   neighborhood, rating:1..5, favorite:bool, lat, lng, note,
 *   tags:[...], images:[url,url,url] }  // images optional */
const PLACES = [
  { id: "p1",  name: "Social",                    emoji: "🍻", type: "restaurant", neighborhood: "Hauz Khas Village",      rating: 4, favorite: true,  lat: 28.5535, lng: 77.1991, note: "Hot tennessee chicken wings & Honey chilli fries for life",               tags: ["wings", "bar", "late night", "hauz khas"], images: [] },
  { id: "p2",  name: "Sandoz",                    emoji: "🥂", type: "restaurant", neighborhood: "Connaught Place",         rating: 4, favorite: false, lat: 28.6334, lng: 77.2194, note: "Chicken tikka masala with garlic naan and can of coke, can't get any better than this",               tags: ["north indian", "comfort food", "cp classic"], images: [] },
  // { id: "p3",  name: "United Coffee House",        emoji: "☕", type: "cafe",       neighborhood: "Connaught Place",         rating: 4, favorite: false, lat: 28.6322, lng: 77.2195, note: "edit this note later.",               tags: ["heritage", "filter coffee", "cp", "old school"], images: [] },
  { id: "p4",  name: "California Burrito",         emoji: "🌯", type: "restaurant", neighborhood: "Rohini Sector 18",        rating: 4, favorite: false, lat: 28.7188, lng: 77.1092, note: "Just never enough",               tags: ["mexican", "burrito", "rohini", "quick bite"], images: [] },
  // { id: "p5",  name: "Romeo Lane",                emoji: "🍕", type: "restaurant", neighborhood: "Gurugram",                rating: 4, favorite: false, lat: 28.4735, lng: 77.0636, note: "edit this note later.",               tags: ["pizza", "gurugram", "casual"], images: [] },
  { id: "p6",  name: "AMA Cafe",                  emoji: "☕", type: "cafe",       neighborhood: "Khan Market",             rating: 4, favorite: false, lat: 28.6002, lng: 77.2272, note: "Pan cakes and peri peri fries are the way to go",               tags: ["brunch", "pancakes", "peri peri", "khan market"], images: [] },
  { id: "p7",  name: "Wongden Cafe",              emoji: "🍵", type: "cafe",       neighborhood: "Khan Market",             rating: 4, favorite: false, lat: 28.5998, lng: 77.2268, note: "Pizza's and vibes",               tags: ["tibetan", "cozy", "good vibes", "khan market"], images: [] },
  // { id: "p8",  name: "Kaffiaa Rooftop Cafe",      emoji: "🌇", type: "cafe",       neighborhood: "Noida Sector 18",         rating: 4, favorite: false, lat: 28.5700, lng: 77.3215, note: "edit this note later.",               tags: ["rooftop", "noida", "views", "sunset"], images: [] },
  { id: "p9",  name: "Spezia Bistro",             emoji: "🍝", type: "restaurant", neighborhood: "Noida Sector 104",        rating: 4, favorite: false, lat: 28.5198, lng: 77.3892, note: "Nacho Macho is the real head hancho",               tags: ["italian", "nachos", "noida", "pasta"], images: [] },
  { id: "p10", name: "Galaxy Pizza",              emoji: "🍕", type: "restaurant", neighborhood: "Lajpat Nagar",            rating: 4, favorite: false, lat: 28.5655, lng: 77.2435, note: "Angry Spicy Margreta and Galaxy garlic bread with all their dips and chillie oil",               tags: ["pizza", "spicy", "garlic bread", "lajpat nagar"], images: [] },
  { id: "p11", name: "Paprizza",                  emoji: "🍕", type: "restaurant", neighborhood: "Greater Kailash",         rating: 4, favorite: false, lat: 28.5488, lng: 77.2392, note: "Truffle fries, Veggie Delight or BBQ chicken but NY style is the way to go",               tags: ["ny style pizza", "truffle fries", "bbq chicken", "greater kailash"], images: [] },
  { id: "p12", name: "Tossin Pizza",              emoji: "🍕", type: "restaurant", neighborhood: "Lajpat Nagar",            rating: 4, favorite: false, lat: 28.5660, lng: 77.2440, note: "Just for small bites",               tags: ["pizza", "quick bite", "lajpat nagar"], images: [] },
  { id: "p13", name: "Coffee Home",               emoji: "☕", type: "cafe",       neighborhood: "Connaught Place",         rating: 4, favorite: false, lat: 28.6289, lng: 77.2175, note: "Dosa, filter coffee and winter sun",               tags: ["south indian", "filter coffee", "dosa", "winter morning"], images: [] },
  { id: "p14", name: "Call Chotu",                emoji: "🍽️", type: "restaurant", neighborhood: "Saket",                   rating: 4, favorite: false, lat: 28.5275, lng: 77.2168, note: "Naan style pizza ?? great drinks",               tags: ["naan pizza", "drinks", "saket", "quirky"], images: [] },
  // { id: "p15", name: "Effingut",                  emoji: "🍺", type: "restaurant", neighborhood: "Saket",                   rating: 4, favorite: false, lat: 28.5245, lng: 77.2163, note: "edit this note later.",               tags: ["brewery", "beer", "saket", "loud"], images: [] },
  // { id: "p16", name: "Tavasaa",                   emoji: "🏔️", type: "cafe",       neighborhood: "Dehradun",                rating: 4, favorite: false, lat: 30.3198, lng: 78.0435, note: "edit this note later.",               tags: ["dehradun", "mountain cafe", "scenic", "weekend trip"], images: [] },
  { id: "p17", name: "Melt House Cafe",           emoji: "🧀", type: "cafe",       neighborhood: "Greater Kailash",         rating: 4, favorite: false, lat: 28.5492, lng: 77.2390, note: "Nashville chicken fries, Bianca chicken pizza & Matilda cake and you are set",               tags: ["nashville chicken", "pizza", "cake", "greater kailash"], images: [] },
  { id: "p18", name: "Big Chill",                 emoji: "🍝", type: "restaurant", neighborhood: "Khan Market",             rating: 5, favorite: true,  lat: 28.5996, lng: 77.2266, note: "OVERRATED",               tags: ["overrated", "pasta", "khan market", "everyone goes here"], images: [] },
  // { id: "p19", name: "The GT Road",               emoji: "🥃", type: "restaurant", neighborhood: "Connaught Place",         rating: 4, favorite: false, lat: 28.6315, lng: 77.2188, note: "WAYYY",               tags: ["bar", "whiskey", "cp", "late night"], images: [] },
  // { id: "p20", name: "Zoya",                      emoji: "✨", type: "restaurant", neighborhood: "Jaipur",                  rating: 4, favorite: false, lat: 26.9124, lng: 75.7873, note: "edit this note later.",               tags: ["jaipur", "fine dining", "date night", "travel"], images: [] },
  { id: "p21", name: "Leo's Pizzeria",              emoji: "🍕", type: "restaurant", neighborhood: "Vasant Vihar",            rating: 4, favorite: false, lat: 28.5572, lng: 77.1573, note: "Piccante and Diavolo for pizza best in town, tiramisu for dessert",               tags: ["pizza", "tiramisu", "italian", "vasant vihar"], images: [] },
];

/* ---- helpers ----------------------------------------------------------- */
const clamp = (n) => Math.max(0, Math.min(5, Math.round(n)));
const stars = (n) => "★★★★★".slice(0, clamp(n)) + "☆☆☆☆☆".slice(0, 5 - clamp(n));

const pinIcon = (place, selected) => {
  const color = selected ? SEL : place.type === "cafe" ? CAFE : REST;
  return L.divIcon({
    className: "",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
    html: `<div style="width:38px;height:38px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 3px 9px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;font-size:18px;cursor:pointer;transition:transform .16s;${selected ? "transform:scale(1.18);" : ""}">${place.emoji}</div>`,
  });
};

/* Applies the warm CSS filter to the tile pane + pans to selection */
function MapEffects({ selectedId, places }) {
  const map = useMap();
  React.useEffect(() => {
    const pane = map.getPane("tilePane");
    if (pane && !MAPBOX_TOKEN) {
      pane.style.filter = `sepia(${(0.8 * WARMTH).toFixed(2)}) saturate(${(1 + 0.7 * WARMTH).toFixed(2)}) brightness(1.03) contrast(0.95) hue-rotate(-8deg)`;
    }
  }, [map]);
  React.useEffect(() => {
    const p = places.find((x) => x.id === selectedId);
    if (p) map.panTo([p.lat, p.lng], { animate: true, duration: 0.5 });
  }, [map, selectedId, places]);
  return null;
}

/* ---- component --------------------------------------------------------- */
export default function RestaurantMap({ places = PLACES }) {
  const [selectedId, setSelectedId] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const mapRef = useRef(null);
  const S = styles;

  const stats = useMemo(() => ({
    total: places.length,
    cafes: places.filter((p) => p.type === "cafe").length,
    favorites: places.filter((p) => p.favorite).length,
  }), [places]);

  const selected = places.find((p) => p.id === selectedId) || null;
  const selImages = selected && Array.isArray(selected.images) ? selected.images : [];

  return (
    <div style={S.root}>
      <MapContainer center={CENTER} zoom={ZOOM} zoomControl={false} style={{ position: "absolute", inset: 0, background: "#e6dbc6" }} ref={mapRef}>
        <TileLayer
          url={MAPBOX_TOKEN
            ? `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE}/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`
            : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"}
          subdomains="abcd"
          maxZoom={20}
          attribution={MAPBOX_TOKEN ? "&copy; Mapbox &copy; OpenStreetMap" : "&copy; OpenStreetMap &copy; CARTO"}
        />
        <MapEffects selectedId={selectedId} places={places} />
        {places.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p, p.id === selectedId)} eventHandlers={{ click: () => setSelectedId(p.id) }} />
        ))}
      </MapContainer>

      {/* title card */}
      <div style={S.title}>
        {/* <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: "-.02em" }}>{OWNER}</div> */}
        <div style={{ fontSize: 13.5, color: "#b6aa93", marginTop: 4 }}>{TAGLINE}</div>
        <div style={{ display: "flex", gap: 26, marginTop: 18 }}>
          {[["places", stats.total], ["cafes", stats.cafes], ["favorites", stats.favorites]].map(([label, n]) => (
            <div key={label}>
              <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1 }}>{n}</div>
              <div style={{ fontSize: 11, color: "#9b9080", marginTop: 5 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* legend */}
      <div style={S.legend}>
        {[["restaurant", REST], ["cafe", CAFE], ["selected", SEL]].map(([label, c]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: c, border: "2px solid #fff", boxSizing: "content-box" }} />
            {label}
          </div>
        ))}
      </div>

      {/* all places button */}
      <button style={S.fab} onClick={() => setListOpen((v) => !v)}>≡ all places</button>

      {/* detail card */}
      {selected && (
        <div style={{ ...S.detail, right: listOpen ? 356 : 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
              <div style={S.detailEmoji}>{selected.emoji}</div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 17, fontWeight: 600 }}>{selected.name}</div>
                  {selected.favorite && <span style={{ color: "#e8785e", fontSize: 13 }}>♥</span>}
                </div>
                <div style={{ fontSize: 12, color: "#a89c88", marginTop: 3 }}>{selected.type} · {selected.neighborhood}</div>
              </div>
            </div>
            <button style={S.x} onClick={() => setSelectedId(null)}>×</button>
          </div>

          <div style={{ marginTop: 12, color: "#e8b64a", fontSize: 15, letterSpacing: 2 }}>{stars(selected.rating)}</div>

          {/* sticky note */}
          <div style={S.sticky}>
            <div style={S.tape} />
            <div style={{ fontFamily: "'Caveat', cursive", fontSize: 20, lineHeight: 1.35, fontWeight: 600 }}>“{selected.note}”</div>
          </div>

          {/* tags */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 16 }}>
            {(selected.tags || []).map((t) => <span key={t} style={S.tag}>{t}</span>)}
          </div>

          {/* photo collage from JSON */}
          {selImages.length > 0 ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 16 }}>
              {selImages.map((src, i) => (
                <img key={i} src={src} alt="" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 8, display: "block", background: "#221c17" }} />
              ))}
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginTop: 16 }}>
                {[0, 1, 2].map((i) => <div key={i} style={S.photo} />)}
              </div>
              <div style={{ fontFamily: "monospace", fontSize: 10, color: "#7a7062", marginTop: 7, textAlign: "center" }}>no photos yet</div>
            </>
          )}
        </div>
      )}

      {/* list panel */}
      {listOpen && (
        <div style={S.panel}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "20px 20px 14px" }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>all places</div>
              <div style={{ fontSize: 12, color: "#9b9080", marginTop: 2 }}>{stats.total} spots on the map</div>
            </div>
            <button style={S.close} onClick={() => setListOpen(false)}>×</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "4px 12px 16px" }}>
            {places.map((p) => (
              <div key={p.id} onClick={() => setSelectedId(p.id)} style={{ ...S.row, background: p.id === selectedId ? "#2a231d" : "transparent" }}>
                <div style={{ ...S.rowDot, background: p.type === "cafe" ? CAFE : REST }}>{p.emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                  <div style={{ fontSize: 11.5, color: "#9b9080", marginTop: 2 }}>{p.type} · {p.neighborhood}</div>
                </div>
                <div style={{ fontSize: 11, color: "#e8b64a", letterSpacing: 1 }}>{stars(p.rating)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- styles ------------------------------------------------------------ */
const cardBase = { background: INK, color: "#f3ede1", border: "1px solid rgba(255,255,255,.07)", boxShadow: "0 14px 40px rgba(26,21,18,.32)" };
const styles = {
  root: { position: "relative", width: "100%", height: "100vh", minHeight: 600, overflow: "hidden", fontFamily: "'Schibsted Grotesk', system-ui, sans-serif", background: "#e6dbc6", color: INK },
  title: { ...cardBase, position: "absolute", top: 22, left: 22, zIndex: 500, width: 266, borderRadius: 18, padding: "20px 22px" },
  legend: { ...cardBase, position: "absolute", bottom: 22, left: 22, zIndex: 500, borderRadius: 13, padding: "11px 15px", display: "flex", gap: 18, fontSize: 12, color: "#e9e1d3" },
  fab: { ...cardBase, position: "absolute", bottom: 22, right: 22, zIndex: 550, borderRadius: 13, padding: "12px 17px", fontSize: 13.5, fontWeight: 500, cursor: "pointer" },
  detail: { ...cardBase, position: "absolute", top: 22, zIndex: 700, width: 312, borderRadius: 18, padding: 20, boxShadow: "0 18px 50px rgba(26,21,18,.42)" },
  detailEmoji: { width: 40, height: 40, borderRadius: "50%", background: "#2a231d", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 21 },
  x: { flex: "none", background: "none", border: "none", color: "#8a8072", fontSize: 20, cursor: "pointer" },
  sticky: { position: "relative", margin: "16px 2px 4px", background: "#f7e18a", color: "#4d3f16", borderRadius: 3, padding: "16px 18px 18px", boxShadow: "0 8px 18px rgba(0,0,0,.28)", transform: "rotate(-1.4deg)" },
  tape: { position: "absolute", top: -9, left: "50%", transform: "translateX(-50%) rotate(2deg)", width: 64, height: 18, background: "rgba(255,255,255,.35)", border: "1px solid rgba(255,255,255,.4)" },
  tag: { fontSize: 11.5, color: "#d8cdb9", background: "#2a231d", border: "1px solid rgba(255,255,255,.06)", padding: "4px 10px", borderRadius: 20 },
  photo: { aspectRatio: "1", border: "1px dashed #4a4136", borderRadius: 8, background: "repeating-linear-gradient(45deg,#221c17,#221c17 6px,#1e1813 6px,#1e1813 12px)" },
  panel: { ...cardBase, position: "absolute", top: 0, right: 0, height: "100%", width: 334, zIndex: 600, borderRadius: 0, borderLeft: "1px solid rgba(255,255,255,.06)", boxShadow: "-14px 0 44px rgba(26,21,18,.34)", display: "flex", flexDirection: "column" },
  close: { width: 34, height: 34, borderRadius: 10, background: "#2a231d", color: "#c9bea9", border: "1px solid rgba(255,255,255,.07)", fontSize: 17, cursor: "pointer" },
  row: { display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 12, cursor: "pointer", marginBottom: 4 },
  rowDot: { flex: "none", width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, border: "2px solid #fff" },
};

/*
 * FONTS: "Schibsted Grotesk" (UI) + "Caveat" (sticky note).
 * Add to index.html <head>:
 *   <link href="https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:wght@400;500;600;700&family=Caveat:wght@500;600;700&display=swap" rel="stylesheet">
 */
